import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  FormField,
  FormPage,
  pagesFromForm,
  visibleFieldsOnPage,
  visiblePageIndexes,
  isShowIfMet,
} from './preChatFormStructure';
import { API_BASE_URL } from '../config/api';

const API_BASE = API_BASE_URL;

function isMissingRequired(field: FormField, values: Record<string, any>): boolean {
  if (field.type === 'note' || !field.required) return false;
  if (!isShowIfMet(field.show_if, values)) return false;
  const v = values[field.id];
  if (Array.isArray(v)) return v.length === 0;
  if (field.type === 'acknowledge' || field.type === 'checkbox') return v !== true;
  return v === undefined || v === null || v === '';
}

function validateNumberLimits(field: FormField, value: any): string | null {
  if (field.type !== 'number' && field.type !== 'decimal') return null;
  if (value === undefined || value === null || value === '') return null;
  const num = Number(value);
  if (Number.isNaN(num)) return `${field.label} must be a number`;
  const min = field.validation?.min;
  const max = field.validation?.max;
  if (min !== undefined && min !== null && num < Number(min)) {
    return `${field.label} must be at least ${min}`;
  }
  if (max !== undefined && max !== null && num > Number(max)) {
    return `${field.label} must be at most ${max}`;
  }
  return null;
}

const fieldControlClass =
  'mt-0.5 flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 shadow-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50';

const PublicPreChatFormPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<any>(null);
  const [pages, setPages] = useState<FormPage[]>([]);
  const [pageIdx, setPageIdx] = useState(0);
  const [values, setValues] = useState<Record<string, any>>({});
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  /** Gate before form fields — not part of the form definition */
  const [gateDone, setGateDone] = useState(false);
  const [isReturning, setIsReturning] = useState<boolean | null>(null);
  const [customerCode, setCustomerCode] = useState('');
  const [returningContactId, setReturningContactId] = useState<number | null>(null);
  const [savedContactId, setSavedContactId] = useState<number | null>(null);

  useEffect(() => {
    if (!slug) return;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        setPageIdx(0);
        setDone(false);
        setGateDone(false);
        setIsReturning(null);
        setCustomerCode('');
        setReturningContactId(null);
        setSavedContactId(null);
        const res = await axios.get(`${API_BASE}/pre-chat-forms/public/by-slug/${slug}`);
        const normalized = pagesFromForm(res.data);
        setForm(res.data);
        setPages(normalized);
        setValues({});
      } catch (e: any) {
        setError(e?.response?.data?.detail || 'Form not found');
        setForm(null);
        setPages([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const primary = form?.primary_color || '#1f93ff';

  const visibleIndexes = useMemo(
    () => visiblePageIndexes(pages, values),
    [pages, values],
  );

  // Keep pageIdx on a visible page when answers change
  useEffect(() => {
    if (!visibleIndexes.length) return;
    if (!visibleIndexes.includes(pageIdx)) {
      setPageIdx(visibleIndexes[0]);
    }
  }, [visibleIndexes, pageIdx]);

  const current = pages[pageIdx] || null;
  const visibleBlocks = useMemo(
    () => (current ? visibleFieldsOnPage(current, values) : []),
    [current, values],
  );

  const visiblePos = Math.max(0, visibleIndexes.indexOf(pageIdx));
  const isFirstVisible = visiblePos <= 0;
  const isLastVisible = visiblePos >= visibleIndexes.length - 1;

  const validateCurrentPage = (): boolean => {
    if (!current) return true;
    for (const block of visibleFieldsOnPage(current, values)) {
      for (const field of block.fields) {
        if (isMissingRequired(field, values)) {
          setError(`Please fill required field: ${field.label}`);
          return false;
        }
        const limitErr = validateNumberLimits(field, values[field.id]);
        if (limitErr) {
          setError(limitErr);
          return false;
        }
      }
    }
    setError(null);
    return true;
  };

  const handleNext = () => {
    if (!validateCurrentPage()) return;
    const nextPos = visiblePos + 1;
    if (nextPos < visibleIndexes.length) setPageIdx(visibleIndexes[nextPos]);
  };

  const handleBack = () => {
    setError(null);
    const prevPos = visiblePos - 1;
    if (prevPos >= 0) setPageIdx(visibleIndexes[prevPos]);
  };

  const submit = async () => {
    if (!slug) return;
    if (!validateCurrentPage()) return;

    for (const page of pages) {
      for (const block of visibleFieldsOnPage(page, values)) {
        for (const field of block.fields) {
          if (isMissingRequired(field, values)) {
            setError(`Please fill required field: ${field.label}`);
            return;
          }
          const limitErr = validateNumberLimits(field, values[field.id]);
          if (limitErr) {
            setError(limitErr);
            return;
          }
        }
      }
    }

    setError(null);
    setSubmitting(true);
    try {
      const payload: Record<string, any> = { form_data: values };
      if (returningContactId != null) {
        payload.contact_id = returningContactId;
      }
      const res = await axios.post(
        `${API_BASE}/pre-chat-forms/public/by-slug/${slug}/submit`,
        payload,
      );
      const cid = Number(res?.data?.contact_id);
      setSavedContactId(Number.isFinite(cid) && cid > 0 ? cid : null);
      setDone(true);
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : detail?.message || 'Submit failed',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const continueAsNew = () => {
    setReturningContactId(null);
    setIsReturning(false);
    setGateDone(true);
    setError(null);
  };

  const continueAsReturning = () => {
    const code = customerCode.trim();
    if (!code) {
      setError('Enter your customer code to continue, or choose “I’m new”.');
      return;
    }
    const n = Number(code);
    if (!Number.isInteger(n) || n <= 0) {
      setError('Customer code must be a positive number.');
      return;
    }
    setReturningContactId(n);
    setIsReturning(true);
    setGateDone(true);
    setError(null);
  };

  const renderField = (field: FormField) => {
    const min = field.validation?.min;
    const max = field.validation?.max;

    return (
      <div key={field.id} className="space-y-1.5">
        {field.type !== 'acknowledge' && field.type !== 'note' && field.type !== 'checkbox' && (
          <Label className="text-sm font-medium text-slate-800">
            {field.label}
            {field.required ? <span className="text-red-500 ml-0.5">*</span> : null}
            {field.type === 'number' && (min != null || max != null) && (
              <span className="ml-1.5 text-xs text-slate-400 font-normal">
                (
                {min != null && max != null
                  ? `${min}–${max}`
                  : min != null
                    ? `≥ ${min}`
                    : `≤ ${max}`}
                )
              </span>
            )}
          </Label>
        )}

        {field.type === 'note' ? (
          <p className="text-sm text-slate-500">{field.label}</p>
        ) : field.type === 'textarea' ? (
          <textarea
            className={`${fieldControlClass} min-h-[80px] h-auto`}
            style={{ ['--tw-ring-color' as any]: primary }}
            value={values[field.id] || ''}
            onChange={(e) => setValues((v) => ({ ...v, [field.id]: e.target.value }))}
            placeholder={field.placeholder}
          />
        ) : field.type === 'select' || field.type === 'select_one' ? (
          <select
            className={fieldControlClass}
            value={values[field.id] || ''}
            onChange={(e) => setValues((v) => ({ ...v, [field.id]: e.target.value }))}
          >
            <option value="">Select...</option>
            {(field.options || []).map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        ) : field.type === 'checkbox' || field.type === 'acknowledge' ? (
          <label className="mt-1 flex items-center gap-2 text-sm text-slate-800">
            <input
              type="checkbox"
              className="h-4 w-4 accent-blue-600"
              checked={!!values[field.id]}
              onChange={(e) => setValues((v) => ({ ...v, [field.id]: e.target.checked }))}
            />
            {field.label}
            {field.required ? <span className="text-red-500">*</span> : null}
          </label>
        ) : field.type === 'rating' ? (
          <div className="mt-0.5 flex gap-1">
            {Array.from({ length: field.validation?.max_stars || 5 }).map((_, i) => {
              const n = i + 1;
              return (
                <button
                  key={n}
                  type="button"
                  className="text-2xl"
                  style={{ color: primary }}
                  onClick={() => setValues((v) => ({ ...v, [field.id]: n }))}
                >
                  {(values[field.id] || 0) >= n ? '★' : '☆'}
                </button>
              );
            })}
          </div>
        ) : field.type === 'file' ? (
          <Input
            type="file"
            className={`${fieldControlClass} bg-white text-slate-900`}
            onChange={(e) =>
              setValues((v) => ({ ...v, [field.id]: e.target.files?.[0]?.name || '' }))
            }
          />
        ) : (
          <Input
            className={`${fieldControlClass} bg-white text-slate-900 border-slate-200`}
            type={
              field.type === 'datetime'
                ? 'datetime-local'
                : field.type === 'tel'
                  ? 'tel'
                  : field.type === 'email'
                    ? 'email'
                    : field.type === 'number' || field.type === 'decimal'
                      ? 'number'
                      : field.type === 'date' || field.type === 'time'
                        ? field.type
                        : 'text'
            }
            min={min}
            max={max}
            step={field.type === 'decimal' ? 'any' : undefined}
            value={values[field.id] ?? ''}
            onChange={(e) => setValues((v) => ({ ...v, [field.id]: e.target.value }))}
            placeholder={field.placeholder}
          />
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="light min-h-screen flex items-center justify-center bg-slate-100 text-slate-600">
        Loading…
      </div>
    );
  }

  if ((error && !form) || (!form && !loading)) {
    return (
      <div className="light min-h-screen flex items-center justify-center bg-slate-100 p-6 text-slate-900">
        <div className="max-w-md w-full rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <h1 className="text-xl font-semibold mb-2 text-slate-900">Form unavailable</h1>
          <p className="text-sm text-slate-500">{error || 'This form link is invalid or no longer available.'}</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="light min-h-screen flex items-center justify-center bg-slate-100 p-4 text-slate-900">
        <div className="max-w-md w-full rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
          <div className="px-6 py-4 text-white text-center" style={{ background: primary }}>
            <h1 className="text-lg font-semibold">{form.title}</h1>
          </div>
          <div className="p-8 text-center space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">Thank you</h2>
            <p className="text-sm text-slate-500">Your response was submitted.</p>
            {savedContactId != null ? (
              <div className="rounded-xl border-2 border-slate-200 bg-slate-50 px-4 py-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                  Your customer code
                </p>
                <p className="text-3xl font-mono font-bold text-slate-900 tracking-wide">
                  {savedContactId}
                </p>
                <p className="text-xs text-slate-500 mt-2 leading-relaxed">
                  Save this code. Next time, choose “returning customer” and enter it to link your answers to the same profile.
                </p>
              </div>
            ) : (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Submitted, but no customer code was returned. Try again or contact support.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!gateDone && form) {
    return (
      <div className="light min-h-[100dvh] flex items-center justify-center bg-slate-100 p-0 sm:p-4 text-slate-900">
        <div className="w-full max-w-md min-h-[100dvh] sm:min-h-0 rounded-none sm:rounded-2xl border-0 sm:border border-slate-200 bg-white shadow-none sm:shadow-sm overflow-hidden flex flex-col">
          <div className="px-6 py-4 text-white text-center" style={{ background: primary }}>
            <h1 className="text-lg font-semibold leading-snug">{form.title}</h1>
          </div>
          <div className="flex-1 flex flex-col px-6 pt-6 pb-6">
            <h2 className="text-base font-semibold text-slate-900 text-center mb-1">
              Are you a returning customer?
            </h2>
            <p className="text-sm text-slate-500 text-center mb-5 leading-relaxed">
              If you have a customer code from a previous visit, enter it below. This is not one of
              the form questions — you can also continue as new.
            </p>

            {isReturning !== false && (
              <div className="space-y-2 mb-4">
                <Label className="text-sm font-medium text-slate-800">Customer code</Label>
                <Input
                  className={`${fieldControlClass} bg-white text-slate-900 border-slate-200 font-mono`}
                  inputMode="numeric"
                  placeholder="e.g. 812"
                  value={customerCode}
                  onChange={(e) => setCustomerCode(e.target.value)}
                  onFocus={() => setIsReturning(true)}
                />
              </div>
            )}

            {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

            <div className="mt-auto flex flex-col gap-2 pt-2">
              <Button
                className="w-full text-white"
                style={{ background: primary }}
                onClick={continueAsReturning}
              >
                Continue with code
              </Button>
              <Button variant="outline" className="w-full" onClick={continueAsNew}>
                I&apos;m new — skip this
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!current) {
    return (
      <div className="light min-h-screen flex items-center justify-center bg-slate-100 p-6 text-slate-900">
        <div className="max-w-md w-full rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <h1 className="text-xl font-semibold mb-2 text-slate-900">Form unavailable</h1>
          <p className="text-sm text-slate-500">This form has no questions yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="light min-h-[100dvh] flex items-center justify-center bg-slate-100 p-0 sm:p-4 text-slate-900">
      <div className="w-full max-w-md min-h-[100dvh] sm:min-h-0 rounded-none sm:rounded-2xl border-0 sm:border border-slate-200 bg-white shadow-none sm:shadow-sm overflow-hidden flex flex-col">
        {/* Title header — centered; no page title in body */}
        <div className="px-6 py-4 text-white text-center" style={{ background: primary }}>
          <h1 className="text-lg font-semibold leading-snug">{form.title}</h1>
        </div>

        <div className="flex-1 flex flex-col px-6 pt-5 pb-4">
          {/* Description on page 1 only (first visible page) */}
          {isFirstVisible && form.subtitle && (
            <p className="text-sm text-slate-500 text-center mb-4 leading-relaxed">
              {form.subtitle}
            </p>
          )}

          <div className="space-y-5 mb-4">
            {visibleBlocks.map(({ section, fields }) => (
              <div key={section.id} className="space-y-3">
                {!section.page_body && section.title ? (
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                    {section.title}
                  </p>
                ) : null}
                <div className="space-y-4">{fields.map(renderField)}</div>
              </div>
            ))}
          </div>

          {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

          <div className="mt-auto flex gap-3 pt-2">
            {!isFirstVisible && (
              <Button
                variant="outline"
                className="flex-1"
                disabled={submitting}
                onClick={handleBack}
                style={{ borderColor: `${primary}59`, color: primary }}
              >
                Back
              </Button>
            )}
            {isLastVisible ? (
              <Button
                className="flex-1 text-white"
                style={{ background: primary }}
                disabled={submitting}
                onClick={submit}
              >
                {submitting ? 'Submitting…' : 'Submit'}
              </Button>
            ) : (
              <Button
                className="flex-1 text-white"
                style={{ background: primary }}
                onClick={handleNext}
              >
                Next
              </Button>
            )}
          </div>

          <p className="text-xs text-slate-400 text-center mt-4 font-medium">
            Page {visiblePos + 1} of {Math.max(visibleIndexes.length, 1)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PublicPreChatFormPage;
